import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  getSortedRowModel,
  type VisibilityState,
} from '@tanstack/react-table'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import { expandedTableLabels } from '../CarPark/components/CarParkTable/CarParkExpandedRow'
import { SortingArrows } from '../icons'

type GenericTableProps<T> = {
  data: T[]
  columns: ColumnDef<T, any>[]
  renderExpandedRow?: (row: T) => React.ReactNode
  resetExpanded?: boolean
  getRowId?: (row: T, index: number) => string
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void
  tableMeta?: any
}

const ExpandableTable = <T,>({
  data,
  columns,
  renderExpandedRow,
  resetExpanded = false,
  getRowId,
  columnVisibility,
  onColumnVisibilityChange,
  tableMeta,
}: GenericTableProps<T>) => {
  const [expanded, setExpanded] = useState({})
  const [sorting, setSorting] = useState([])

  const table = useReactTable({
    data,
    columns,
    state: { expanded, sorting, columnVisibility },
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    onColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => !!renderExpandedRow,
    ...(getRowId && { getRowId }),
    autoResetPageIndex: false,
    autoResetExpanded: false,
    meta: tableMeta,
  })

  const prevResetRef = useRef<boolean | undefined>(undefined)

  useEffect(() => {
    if (resetExpanded !== prevResetRef.current) {
      setExpanded({})
    }
    prevResetRef.current = resetExpanded
  }, [resetExpanded])

  return (
    <div className="min-w-[200px] w-full popUpFromBottom bg-white px-4 pb-4 border-[1px] border-[#B8C4E0] rounded-lg flex flex-col overflow-x-hidden">
      <table className="w-full">
        <thead className="sticky top-0 z-[2] bg-white h-12">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="pt-4 px-4 bg-white">
              {group.headers.map((header) => (
                <th
                  key={header.id}
                  className={`text-left px-4 py-2 text-sm font-semibold whitespace-normal break-words ${
                    header.column.getCanSort() ? 'cursor-pointer' : ''
                  }`}
                  onClick={header.column.getToggleSortingHandler?.()}
                >
                  {header.isPlaceholder ? null : (
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <div className="scale-75">
                        {header.column.getCanSort() &&
                          {
                            asc: <SortingArrows sorting="asc" />,
                            desc: <SortingArrows sorting="desc" />,
                            false: <SortingArrows sorting="none" />,
                          }[String(header.column.getIsSorted()) as 'asc' | 'desc' | 'false']}
                      </div>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => {
            const isExpanded = row.getIsExpanded()
            const isLast = index === table.getRowModel().rows.length - 1

            return (
              <React.Fragment key={row.id}>
                <tr
                  className={
                    (isExpanded && 'bg-gray-50') ||
                    (!isExpanded && !isLast && 'border-b border-old-stroke-secondary') ||
                    ''
                  }
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <td
                      key={`${index}_${cell.id}`}
                      className="px-4 py-2 text-left whitespace-normal break-words align-top"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
                {isExpanded && renderExpandedRow && (
                  <tr className="border-b border-old-stroke-secondary bg-gray-50">
                    {renderExpandedRow(row.original)}
                    {Array.from({
                      length: row.getVisibleCells().length - expandedTableLabels.length,
                    }).map((_, i) => (
                      <td key={`empty-${i}`} className="px-4 py-2" />
                    ))}
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default ExpandableTable
